import {QueryCacheKeys} from '../../api/cache';
import {Queries} from '../../api/queries';
import {getTransactions} from '../../api/transactions';
import {formatCurrency} from '../../formatters';
import {calculateProjectProfit, formatProjectStatus} from '../../helpers/project';
import {List} from '../List';
import MobileFriendlyItemDisplay from '../MobileFriendlyItemDisplay';
import Table from '../Table';
import DollarAmount from '../visual/DollarAmount';
import {Link} from '@sproutsocial/racine';
import {useQuery} from '@tanstack/react-query';
import {SyntheticEvent, useCallback, useMemo} from 'react';
import {formatShortDate} from 'shared/src/datetime';
import {orderBy} from 'shared/src/list';
import {keyBy} from 'shared/src/map';
import {Project} from 'shared/src/models';

interface ProjectsTableProps {
	projects: Project[];
	showCustomerName?: boolean;
	showProfit?: boolean;
}

const ProjectsTable = ({
	projects,
	showCustomerName = true,
	showProfit = false,
}: ProjectsTableProps) => {
	const projectIds = projects.map((project) => project.id);

	const {data: transactions} = useQuery({
		queryKey: QueryCacheKeys.transactions({projectIds}),
		queryFn: () => getTransactions({projectIds}),
		select: (transactions) => keyBy(transactions, (transaction) => transaction.id),
	});

	const {data: customers} = useQuery({
		...Queries.customers(),
		select: (customers) => keyBy(customers, (customer) => customer.id),
	});

	const sortedProjects = useMemo(() => {
		return orderBy(projects, 'startDate', 'desc');
	}, [projects]);

	const onClickListItem = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
		window.location.assign(`/projects/${event.currentTarget.dataset['projectId']}`);
	}, []);

	return (
		<MobileFriendlyItemDisplay
			list={
				<List id='projects'>
					{sortedProjects.map((project) => {
						const projectTransactions =
							project.transactions
								.map((transactionId) => transactions?.get(transactionId))
								.filter((transaction) => transaction !== undefined) ?? [];

						return (
							<List.Item
								key={project.id}
								title={project.name}
								description={project.description}
								data={formatCurrency(
									calculateProjectProfit({
										...project,
										transactions: projectTransactions,
									}),
								)}
								data-project-id={project.id}
								onClick={onClickListItem}
							/>
						);
					})}
				</List>
			}
			table={
				<Table
					id='projects'
					head={[
						{id: 'name', content: 'Name'},
						{id: 'description', content: 'Description'},
						{id: 'status', content: 'Status'},
						{id: 'startDate', content: 'Start Date'},
						...(showCustomerName ? [{id: 'customer', content: 'Customer'}] : []),
						...(showProfit ? [{id: 'profit', content: 'Profit'}] : []),
					]}
					items={sortedProjects}
					generateRow={(project) => {
						const projectTransactions =
							project.transactions
								.map((transactionId) => transactions?.get(transactionId))
								.filter((transaction) => transaction !== undefined) ?? [];

						const customer = customers?.get(project.customer);

						return {
							id: String(project.id),
							cells: [
								<Link href={`/projects/${project.id}`}>{project.name}</Link>,
								project.description,
								formatProjectStatus(project.status),
								formatShortDate(project.startDate),
								...(showCustomerName ? [customer?.name] : []),
								...(showProfit
									? [
											<DollarAmount
												value={calculateProjectProfit({
													...project,
													transactions: projectTransactions,
												})}
											/>,
										]
									: []),
							],
						};
					}}
				/>
			}
		/>
	);
};

export default ProjectsTable;
